import * as React from 'react';
import {
    ArrowCircleLeft24Regular,
    Add24Regular,
    Delete24Regular,
    Save24Regular,
    PresenceAvailableRegular,
    PresenceBlockedRegular,
    DeleteRegular,
    DismissRegular
} from "@fluentui/react-icons";
import {
    InfoLabel,
    Input,
    TableCellLayout,
    TableColumnDefinition,
    TableRowId,
    Subtitle1,
    ToolbarButton,
    createTableColumn,
    makeStyles,
    Link,
    Field,
    InputOnChangeData,
    Tooltip,
    Dropdown,
    Option,
    OptionOnSelectData,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    MessageBarActions,
    Text,
    Button
} from '@fluentui/react-components';
import { Customer, CustomerEnvironment, EnvironmentType } from '../../dto/Customer';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GenericToolbar } from '../../components/GenericToolbar';
import { GenericGrid } from "../../components/GenericGrid";
import { FluentDialog } from "../../components/FluentDialog";
import { flexColumn, flexRow, formInputInlineStyle, formInputStyle, formStyle, infoLabel } from "../../styles/GlobalStyles";
import { useGlobalDispatch } from "../../state/GlobalStateProvider";
import { CopyButton } from "../../components/CopyButton";
import { v4 as uuidv4 } from 'uuid';
import { FormBase } from "./FormBase";
import { fetchRecord, submit } from "../../util/FormUtils";
import { capitalizeFirstChar } from '../../util/StringUtils';
import { IDatabaseRecord } from '../../dto/IDatabaseRecord';
import { ensureSuccess } from '../../util/ErrorHandling';

const entitySet = "customers";

const rowSelector = (item: CustomerEnvironment) => item.guid;

const useStyles = makeStyles({
    formInput: formInputStyle,
    formInputInline: formInputInlineStyle,
    form: formStyle,
    flexRow: flexRow,
    flexColumn: flexColumn,
    infoLabel: infoLabel
});

export const CustomerForm: React.FC<any> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id, publisherId } = useParams();

    const globalDispatch = useGlobalDispatch();
    const [isSaving, setIsSaving] = React.useState(false);
    const [record, setRecord] = React.useState<Customer>({} as Customer);

    const isExisting = !!record?.guid && record.guid !== "new";
    const isDisabled = isExisting && record.state === "inactive";
    const isUserAllowedToSave = React.useMemo(() => (!record.guid || ["contributor", "admin"].includes(record.currentUserAccessLevel)), [record]);
    const isEditable = React.useMemo(() => !isDisabled && isUserAllowedToSave, [isDisabled, isUserAllowedToSave]);
    const areRequiredFieldsMissing = React.useMemo(() => !record.name, [record.name]);
    const isSaveDisabled = React.useMemo(() => isSaving || areRequiredFieldsMissing || !isEditable, [isSaving, areRequiredFieldsMissing, isEditable]);

    const [selectedEnvironments, setSelectedEnvironments] = React.useState<TableRowId[]>([]);
    const [isAddingEnv, setIsAddingEnv] = React.useState(false);
    const [envId, setEnvId] = React.useState("");
    const [envType, setEnvType] = React.useState<EnvironmentType | undefined>();
    const [envName, setEnvName] = React.useState("");
    const [envIdentifier, setEnvIdentifier] = React.useState("");

    const [isConfirmingDeletion, setIsConfirmingDeletion] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [deletionError, setDeletionError] = React.useState("");

    const styles = useStyles();

    const columns: TableColumnDefinition<CustomerEnvironment>[] = React.useMemo(() => [
        createTableColumn<CustomerEnvironment>({
            columnId: "Name",
            compare: (a, b) => {
                return a.name.localeCompare(b.name);
            },
            renderHeaderCell: () => {
                return "Name";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                    {item.name}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerEnvironment>({
            columnId: "Type",
            compare: (a, b) => {
                return a.type.localeCompare(b.type);
            },
            renderHeaderCell: () => {
                return "Type";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout truncate>
                    {item.type}
                    </TableCellLayout>
                );
            },
        }),
        createTableColumn<CustomerEnvironment>({
            columnId: "identifier",
            compare: (a, b) => {
                return a.identifier.localeCompare(b.identifier);
            },
            renderHeaderCell: () => {
                return "Identifier";
            },
            renderCell: (item) => {
                return (
                <TableCellLayout truncate>
                    {item.identifier}
                </TableCellLayout>
                );
            }
        })
    ], []);

    const cancelDialog = React.useCallback(() =>
    {
        setEnvId("");
        setEnvType(undefined);
        setEnvName("");
        setEnvIdentifier("");
        setIsAddingEnv(false);  
    }, []);

    const submitDialog = React.useCallback(() =>
    {
        if (envId)
        {
            setRecord({
                ...record,
                environments: [
                    ...(record.environments ?? []).map(e => e.guid === envId ? ({...e, type: envType as EnvironmentType, identifier: envIdentifier, name: envName}) : e)]
            });
        }
        else {
            const newEnv: CustomerEnvironment = {
                guid: uuidv4(),
                identifier: envIdentifier,
                type: envType as EnvironmentType,
                name: envName
            };
            setRecord({ ...record, environments: [...(record.environments ?? []), newEnv] });
        }
        cancelDialog();
    }, [envId, cancelDialog, record, envIdentifier, envName, envType]);

    const editEnv = React.useCallback((envId: TableRowId) =>
    {
        const env = record.environments.find(e => e.guid === envId.toString());

        if (env)
        {
            setEnvId(env.guid);
            setEnvType(env.type);
            setEnvName(env.name);
            setEnvIdentifier(env.identifier);
            setIsAddingEnv(true);
        }
    }, [record.environments]);

    const onEnvironmentSelection = React.useCallback((selection: TableRowId[]) =>
    {
        setSelectedEnvironments(selection);
    }, []);

    const onEnvironmentDeletion = React.useCallback(() =>
    {
        setRecord({
            ...record,
            environments: [
                ...(record.environments ?? []).filter(e => !selectedEnvironments.includes(e.guid))]
        });
    }, [record, selectedEnvironments]);

    const onEnvironmentTypeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        setEnvType(data.optionValue! as EnvironmentType);
    }, []);

    const onEnvironmentNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setEnvName(data.value);
    }, []);

    const onEnvironmentOrgIdChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setEnvIdentifier(data.value);
    }, []);

    const onNameChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setRecord((record) => ({...record, name: data.value}));
    }, []);

    const onBack = React.useCallback(() =>
    {
        navigate(`/workspace/publishers/${publisherId}/${entitySet}`);
    }, [navigate, publisherId]);

    const onSubmit = React.useCallback(async () =>
    {
        if (!isSaveDisabled)
        {
            await submit(publisherId, id, record, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
    }, [globalDispatch, id, isSaveDisabled, navigate, publisherId, record]);

    const onStateToggle = React.useCallback(async () =>
    {
        if (!isDisabled)
        {
            const update = ({...record, state: "inactive"} as IDatabaseRecord);
            await submit(publisherId, id, update, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
        else
        {
            const update = ({...record, state: "active"} as IDatabaseRecord);
            await submit(publisherId, id, update, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
    }, [globalDispatch, id, isDisabled, navigate, publisherId, record]);

    const onDelete = React.useCallback(() =>
    {
        setIsConfirmingDeletion(true);
    }, []);

    const clearDeletionError = React.useCallback(() =>
    {
        setDeletionError("");
    }, []);

    const cancelDeletionDialog = React.useCallback(() =>
    {
        clearDeletionError();
        setIsConfirmingDeletion(false);
    }, [clearDeletionError]);

    const submitDeletionDialog = React.useCallback(async () =>
    {
        try
        {
            setIsDeleting(true);

            const response = await fetch(`/api/publishers/${publisherId}/${entitySet}/${id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            await ensureSuccess(response);
            
            cancelDeletionDialog();
            onBack();
        }
        catch (e)
        {
            const error = e as Error;
            setDeletionError(error.message ?? "Unspecified Error");
        }
        finally
        {
            setIsDeleting(false);
        }
    }, [cancelDeletionDialog, id, onBack, publisherId]);

    const didInitRef = React.useRef<string | null>(null);

    // This is the initialization hook
    React.useEffect(() =>
    {
        // If we already initialized once, just exit, updates supply us with the latest record state automatically
        if (didInitRef.current !== id)
        {
            if (!id)
            {
                return;
            }

            // This happens right after the transition from id === "new" to the actual id
            if (location?.state?.record?.guid === id)
            {
                // Use record from location once...
                setRecord(location.state.record);

                //...then discard so that it can never overwrite local edits
                navigate(location.pathname, {
                    replace: true,
                    state: null
                });
            }
            else
            {
                // In all other cases, just fetch
                fetchRecord(
                    publisherId,
                    id,
                    entitySet,
                    setRecord,
                    globalDispatch
                );
            }

            didInitRef.current = id;
        }
    },
    [
        id,
        publisherId,
        location.pathname,
        location.state,
        navigate,
        globalDispatch
    ]);

    const dialogs = React.useMemo(() => (
        <>
            <FluentDialog title={`Delete this record`} submitLabel='Confirm' open={isConfirmingDeletion} submitDisabled={isDeleting} hideCancel={isDeleting} onSubmit={submitDeletionDialog} onCancel={cancelDeletionDialog}>
                <div className={styles.form}>
                    { deletionError && (
                        <MessageBar intent="error">
                            <MessageBarBody>
                                <MessageBarTitle>An error occured: {deletionError}</MessageBarTitle>
                            </MessageBarBody>
                            <MessageBarActions
                                containerAction={
                                    <Button
                                        aria-label="dismiss"
                                        appearance="transparent"
                                        icon={<DismissRegular />}
                                        onClick={clearDeletionError}
                                    />
                                }
                            />
                        </MessageBar>
                    ) }
                    <Text>
                        Click confirm below to delete this record.
                    </Text>
                </div>
            </FluentDialog>
            <FluentDialog title={`${ envId ? "Edit" : "Add"} Environment`} open={isAddingEnv} submitDisabled={!envName || !envType || !envIdentifier || !isEditable} onSubmit={submitDialog} onCancel={cancelDialog}>
                <div className={styles.form}>
                    <Field
                        label="Name"
                        hint={`${(envName ?? "").length} / 200`}
                        required
                    >
                        <Input maxLength={200} type="text" value={envName ?? ""} onChange={onEnvironmentNameChange} />
                    </Field>

                    <Field
                        label="Type"
                        required
                    >
                        <Dropdown
                            placeholder="Choose a type for the environment"
                            value={capitalizeFirstChar(envType) ?? ""}
                            selectedOptions={envType ? [envType] : []}
                            onOptionSelect={onEnvironmentTypeChange}
                        >
                            <Option key="dataverse" value="dataverse">Dataverse</Option>
                            <Option key="entra" value="entra">Entra</Option>
                        </Dropdown>
                    </Field>

                    <Field
                        label={
                            <InfoLabel
                                info={
                                    <>
                                        { envType === "dataverse" && <>The dataverse organization's associated ID. For information on where to find this, check <Link href="https://learn.microsoft.com/en-us/power-platform/admin/determine-org-id-name" target="_blank">here</Link>.</> }
                                        { envType === "entra" && <>The Entra ID's associated Tenant ID. For information on where to find this, go to make.powerapps.com, click on the "Settings" cog, go to "Session Details" and copy the value after "Tenant ID" from there.</> }
                                    </>
                                }
                            >
                                { envType === "dataverse" ? "Organization ID" : "Tenant ID" }
                            </InfoLabel>
                        }
                        hint={`${(envIdentifier ?? "").length} / 36`}
                        required
                    >
                        <Input maxLength={36} type="text" value={envIdentifier ?? ""} onChange={onEnvironmentOrgIdChange} />
                    </Field>
                </div>
            </FluentDialog>
        </>
    ), [isConfirmingDeletion, isDeleting, submitDeletionDialog, cancelDeletionDialog, styles.form, deletionError, clearDeletionError, envId, isAddingEnv, envName, envType, envIdentifier, isEditable, submitDialog, cancelDialog, onEnvironmentNameChange, onEnvironmentTypeChange, onEnvironmentOrgIdChange]);

    const toolbar = React.useMemo(() => (
        <GenericToolbar
            actionButtons={
                <>
                    <Tooltip relationship='label' content="Back">
                        <ToolbarButton
                            aria-label="Back"
                            appearance="subtle"
                            icon={<ArrowCircleLeft24Regular />}
                            onClick={onBack}
                        />
                    </Tooltip>
                    <Tooltip relationship='label' content="Save (Ctrl + S)">
                        <ToolbarButton
                            aria-label="Save"
                            appearance="primary"
                            icon={<Save24Regular />}
                            disabled={isSaveDisabled}
                            onClick={onSubmit}
                        />
                    </Tooltip>
                    <Tooltip relationship='label' content={ isDisabled ? "Enable" : "Disable"}>
                        <ToolbarButton
                            aria-label={ isDisabled ? "Enable" : "Disable"}
                            appearance="primary"
                            disabled={!isExisting || isSaving || areRequiredFieldsMissing || !isUserAllowedToSave}
                            icon={ isDisabled ? <PresenceAvailableRegular /> : <PresenceBlockedRegular />}
                            onClick={onStateToggle}
                        />
                    </Tooltip>
                    <Tooltip relationship='label' content="Delete">
                        <ToolbarButton
                            aria-label={"Delete"}
                            appearance="primary"
                            disabled={!isExisting || isSaving || record.currentUserAccessLevel !== "admin"}
                            icon={ <DeleteRegular />}
                            onClick={onDelete}
                        />
                    </Tooltip>
                </>
            }
            farGroup={
                <>
                    <Field
                        label={
                            <InfoLabel size="medium" className={styles.infoLabel} info="External representation of this record's id. Visible on generated licenses.">
                                External Id
                            </InfoLabel>
                        }
                    >
                        <Input type="text" value={record.externalId ?? ""} disabled contentAfter={<CopyButton disabled={!record.externalId} value={record.externalId} />} />
                    </Field>
                </>
            }
        />
    ), [onBack, isSaveDisabled, onSubmit, isDisabled, isExisting, isSaving, areRequiredFieldsMissing, isUserAllowedToSave, onStateToggle, record.currentUserAccessLevel, record.externalId, onDelete, styles.infoLabel]);

    const body = React.useMemo(() => (
        <>
            <Field
                label={
                    <InfoLabel size="medium" required className={styles.infoLabel} info="Customer name. Visible on generated licenses.">
                        Name
                    </InfoLabel>
                }
                hint={`${(record.name ?? "").length} / 200`}
            >
                <Input maxLength={200} type="text" value={record.name ?? ""} disabled={!isEditable} onChange={onNameChange} />
            </Field>

            <div style={{ borderRadius: "5px", marginTop: "20px" }}>
                <Subtitle1>Customer Environments</Subtitle1>

                <GenericGrid
                    onRowClick={editEnv}
                    onSelectionChange={onEnvironmentSelection}
                    columns={columns}
                    items={record.environments ?? []}
                    rowSelector={rowSelector}
                    actionButtons={
                        <>
                            <ToolbarButton
                                aria-label="Add new record"
                                appearance="primary"
                                icon={<Add24Regular />}
                                disabled={!isEditable}
                                onClick={() => setIsAddingEnv(true)}
                            />
                            <ToolbarButton
                                aria-label="Delete record"
                                icon={<Delete24Regular />}
                                disabled={!isEditable}
                                onClick={onEnvironmentDeletion}
                            />
                        </>
                    }
                />
            </div>
        </>
    ), [columns, editEnv, isEditable, onEnvironmentDeletion, onEnvironmentSelection, onNameChange, record.environments, record.name, styles.infoLabel]);

    return (
        <FormBase
            id={id}
            record={record}
            isSaving={isSaving}
            dialogs={dialogs}
            toolbar={toolbar}
            body={body}
            onSave={onSubmit}
        />
    );
};